import { Add } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ChipOwnProps,
  Divider,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllUsers } from '../../../../../../features/users';
import {
  createScenario,
  deleteScenario,
  deleteSharedUser,
  listScenarios,
  ScenarioDTO,
  ScenarioSharedUserDTO,
  ScenarioStatus,
  setScenarioStatus,
  shareScenario,
} from '../../../../../../shared/api/scenarioApi';
import { StorageKey } from '../../../../../../shared/auth/DataService';
import DoubleConfirmModal from '../../../../../../shared/components/modals/DoubleConfirmModal';
import MBModal from '../../../../../../shared/components/modals/MBModal';
import { EDITING_LOCK_TIMEOUT_MINUTES } from '../../../../../../shared/constants/scenarioConstants';
import { useScenario } from '../../../../../../shared/contexts/ScenarioContext';
import { getScenarioHighlightColor } from '../../../../../../shared/utils/scenarioHighlight';

const MAX_OWNED_SCENARIOS = 5;

const STATUS_COLOR: Record<ScenarioStatus, ChipOwnProps['color']> = {
  DRAFT: 'default',
  VIEW: 'info',
  EDITING: 'warning',
  ARCHIVED: 'default',
};

const isViewOrEditing = (status: ScenarioStatus) =>
  status === 'VIEW' || status === 'EDITING';

const isEditingLockExpired = (updatedAt: string | null): boolean => {
  if (!updatedAt) return true;
  const expiry =
    new Date(updatedAt).getTime() + EDITING_LOCK_TIMEOUT_MINUTES * 60 * 1000;
  return Date.now() > expiry;
};

interface ScenarioManagerModalProps {
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'error' | 'success' | 'warning';
}

interface LpUserOption {
  emea: string;
  givenName: string | null;
  familyName: string | null;
}

const formatUserLabel = (
  u: LpUserOption | ScenarioSharedUserDTO['sharedUser']
) =>
  u.givenName || u.familyName
    ? `${u.givenName ?? ''} ${u.familyName ?? ''}`.trim() + ` (${u.emea})`
    : u.emea;

interface SharedUsersPanelProps {
  scenario: ScenarioDTO;
  shareableUsers: LpUserOption[];
  isSharing: boolean;
  onShare: (scenario: ScenarioDTO, user: LpUserOption) => Promise<void>;
  onDelete: (scenario: ScenarioDTO, su: ScenarioSharedUserDTO) => Promise<void>;
}

const SharedUsersPanel = ({
  scenario,
  shareableUsers,
  isSharing,
  onShare,
  onDelete,
}: SharedUsersPanelProps) => {
  const [selected, setSelected] = useState<LpUserOption | null>(null);
  const sharedUsers = scenario.scenarioSharedUsers ?? [];

  const handleShare = async () => {
    if (!selected) return;
    await onShare(scenario, selected);
    setSelected(null);
  };

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
        <Autocomplete<LpUserOption>
          size='small'
          value={selected}
          options={shareableUsers}
          getOptionLabel={(opt) => formatUserLabel(opt)}
          isOptionEqualToValue={(opt, val) => opt.emea === val.emea}
          onChange={(_, value) => setSelected(value)}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              label='Add user'
              placeholder='Search by name or EMEA'
            />
          )}
          sx={{ width: 280 }}
          noOptionsText='No users available'
        />
        <Button
          variant='contained'
          size='small'
          startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 16 }} />}
          disabled={isSharing || !selected}
          onClick={handleShare}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Share
        </Button>
      </Stack>

      {sharedUsers.length > 0 ? (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <Typography
            variant='caption'
            color='text.secondary'
            fontWeight={500}
            sx={{ mb: 1, display: 'block' }}
          >
            Shared with ({sharedUsers.length})
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {sharedUsers.map((su) => (
              <Card
                key={su.id}
                variant='outlined'
                sx={{
                  minWidth: 190,
                  maxWidth: 240,
                  borderLeft: '3px solid',
                  borderLeftColor: isViewOrEditing(su.status)
                    ? (theme) => getScenarioHighlightColor(theme)
                    : 'divider',
                }}
              >
                <CardContent
                  sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}
                >
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Box sx={{ overflow: 'hidden', mr: 0.5 }}>
                      <Typography variant='body2' fontWeight={600} noWrap>
                        {su.sharedUser.givenName || su.sharedUser.familyName
                          ? `${su.sharedUser.givenName ?? ''} ${su.sharedUser.familyName ?? ''}`.trim()
                          : su.sharedUser.emea}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        noWrap
                      >
                        {su.sharedUser.emea}
                      </Typography>
                    </Box>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <Chip
                        label={su.status}
                        size='small'
                        color={STATUS_COLOR[su.status] ?? 'default'}
                        variant={
                          isViewOrEditing(su.status) ? 'filled' : 'outlined'
                        }
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      {!isViewOrEditing(su.status) && (
                        <Tooltip title='Remove'>
                          <IconButton
                            size='small'
                            color='error'
                            disabled={isSharing}
                            onClick={() => onDelete(scenario, su)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
};

const ScenarioManagerModal = ({
  open,
  onClose,
  readOnly = false,
}: ScenarioManagerModalProps) => {
  const { activeScenario, setActiveScenario, clearScenario } = useScenario();

  const currentUser = localStorage.getItem(StorageKey.userInfo_sub) ?? '';

  const [scenarios, setScenarios] = useState<ScenarioDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'error',
  });

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ScenarioDTO | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tabIndex, setTabIndex] = useState(readOnly ? 1 : 0);

  const [allUsers, setAllUsers] = useState<LpUserOption[]>([]);
  const [isSharing, setIsSharing] = useState(false);
  const apiRef = useGridApiRef();

  const isActionInProgress = isLoading || isCreating || isDeleting || isSharing;

  const showSnackbar = (
    message: string,
    severity: SnackbarState['severity'] = 'error'
  ) => setSnackbar({ open: true, message, severity });

  const handleClose = () => {
    if (isActionInProgress) {
      showSnackbar(
        'Please wait — an action is in progress. You cannot close the modal right now.',
        'warning'
      );
      return;
    }
    onClose();
  };

  const fetchScenarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listScenarios();
      setScenarios(data ?? []);
    } catch {
      showSnackbar('Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setAllUsers(
        (data ?? []).map((u: any) => ({
          emea: u.emea,
          givenName: u.givenName ?? null,
          familyName: u.familyName ?? null,
        }))
      );
    } catch {
      // non-critical — autocomplete will just be empty
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchScenarios();
      fetchUsers();
      setNewName('');
      setNewDescription('');
      setSnackbar({ open: false, message: '', severity: 'error' });
    }
  }, [open, fetchScenarios, fetchUsers]);

  const [takeoverTarget, setTakeoverTarget] = useState<ScenarioDTO | null>(
    null
  );

  const ownedActiveOrDraftCount = useMemo(
    () =>
      scenarios.filter(
        (s) => s.createdBy === currentUser && s.status !== 'ARCHIVED'
      ).length,
    [scenarios, currentUser]
  );

  const atLimit = ownedActiveOrDraftCount >= MAX_OWNED_SCENARIOS;

  const handleCreate = async () => {
    if (!newName.trim() || atLimit) return;
    setIsCreating(true);
    try {
      await createScenario({
        name: newName.trim(),
        description: newDescription.trim() || null,
      });
      setNewName('');
      setNewDescription('');
      await fetchScenarios();
    } catch {
      showSnackbar('Failed to create scenario');
    } finally {
      setIsCreating(false);
    }
  };

  const ownedViewOrEditingScenario = useMemo(
    () =>
      scenarios.find(
        (s) => s.createdBy === currentUser && isViewOrEditing(s.status)
      ) ?? null,
    [scenarios, currentUser]
  );

  const handleView = async (scenario: ScenarioDTO) => {
    setIsLoading(true);
    try {
      await setScenarioStatus(scenario.id, 'VIEW');
      setActiveScenario({ id: scenario.id, name: scenario.name, mode: 'VIEW' });
      await fetchScenarios();
    } catch {
      showSnackbar('Failed to view scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (scenario: ScenarioDTO) => {
    setIsLoading(true);
    try {
      await setScenarioStatus(scenario.id, 'EDITING');
      setActiveScenario({
        id: scenario.id,
        name: scenario.name,
        mode: 'EDITING',
      });
      await fetchScenarios();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to enter editing mode';
      showSnackbar(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopEditing = async (scenario: ScenarioDTO) => {
    setIsLoading(true);
    try {
      await setScenarioStatus(scenario.id, 'VIEW');
      setActiveScenario({ id: scenario.id, name: scenario.name, mode: 'VIEW' });
      await fetchScenarios();
    } catch {
      showSnackbar('Failed to stop editing');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async (scenario: ScenarioDTO) => {
    setIsLoading(true);
    try {
      await setScenarioStatus(scenario.id, 'DRAFT');
      if (activeScenario?.id === scenario.id) {
        clearScenario();
      }
      await fetchScenarios();
    } catch {
      showSnackbar('Failed to exit scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTakeoverConfirm = async () => {
    if (!takeoverTarget) return;
    setIsLoading(true);
    try {
      await setScenarioStatus(takeoverTarget.id, 'EDITING');
      setActiveScenario({
        id: takeoverTarget.id,
        name: takeoverTarget.name,
        mode: 'EDITING',
      });
      setTakeoverTarget(null);
      await fetchScenarios();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to take over editing';
      showSnackbar(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteScenario(deleteTarget.id);
      if (activeScenario?.id === deleteTarget.id) {
        clearScenario();
      }
      setDeleteTarget(null);
      await fetchScenarios();
    } catch {
      showSnackbar('Failed to delete scenario');
    } finally {
      setIsDeleting(false);
    }
  };

  const isOwnedByMe = (scenario: ScenarioDTO) =>
    scenario.createdBy === currentUser;

  const getMySharedEntry = (scenario: ScenarioDTO) =>
    (scenario.scenarioSharedUsers ?? []).find(
      (su) => su.sharedUser.emea === currentUser
    ) ?? null;

  const getEffectiveStatus = (scenario: ScenarioDTO): ScenarioStatus => {
    if (isOwnedByMe(scenario)) return scenario.status;
    const entry = getMySharedEntry(scenario);
    return entry?.status ?? scenario.status;
  };

  const handleShare = async (scenario: ScenarioDTO, user: LpUserOption) => {
    setIsSharing(true);
    try {
      await shareScenario(scenario.id, user.emea);
      await fetchScenarios();
      showSnackbar(`Scenario shared with ${formatUserLabel(user)}`, 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to share scenario';
      showSnackbar(msg);
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeleteSharedUser = async (
    scenario: ScenarioDTO,
    share: ScenarioSharedUserDTO
  ) => {
    setIsSharing(true);
    try {
      await deleteSharedUser(scenario.id, share.id);
      await fetchScenarios();
      showSnackbar('Shared user removed', 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to remove shared user';
      showSnackbar(msg);
    } finally {
      setIsSharing(false);
    }
  };

  const getShareableUsers = useCallback(
    (scenario: ScenarioDTO): LpUserOption[] => {
      const alreadyShared = new Set(
        (scenario.scenarioSharedUsers ?? []).map((su) => su.sharedUser.emea)
      );
      return allUsers.filter(
        (u) => u.emea !== currentUser && !alreadyShared.has(u.emea)
      );
    },
    [allUsers, currentUser]
  );

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<ScenarioDTO>) => {
      const scenario = params.row;
      if (!isOwnedByMe(scenario)) return null;
      const shareableUsers = getShareableUsers(scenario);

      return (
        <SharedUsersPanel
          scenario={scenario}
          shareableUsers={shareableUsers}
          isSharing={isSharing}
          onShare={handleShare}
          onDelete={handleDeleteSharedUser}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allUsers, scenarios, isSharing, currentUser]
  );

  const getDetailPanelHeight = useCallback(
    (params: GridRowParams<ScenarioDTO>) => {
      if (!isOwnedByMe(params.row)) return 0;
      const sharedCount = (params.row.scenarioSharedUsers ?? []).length;
      return sharedCount > 0 ? 110 + Math.ceil(sharedCount / 3) * 56 : 70;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenarios, currentUser]
  );

  const sortByCreatedAtDesc = (a: ScenarioDTO, b: ScenarioDTO) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const activeOrDraftScenarios = useMemo(
    () =>
      scenarios
        .filter((s) => s.status !== 'ARCHIVED')
        .sort(sortByCreatedAtDesc),
    [scenarios]
  );

  const sharedScenarios = useMemo(
    () =>
      scenarios
        .filter((s) => s.createdBy !== currentUser)
        .sort(sortByCreatedAtDesc),
    [scenarios, currentUser]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.5,
        minWidth: 140,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<ScenarioDTO>) => {
          const owned = isOwnedByMe(params.row);
          const isCurrentlyActive = activeScenario?.id === params.row.id;
          return (
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography
                variant='body2'
                fontWeight={isCurrentlyActive ? 700 : 500}
                noWrap
                sx={{
                  color: isCurrentlyActive
                    ? (theme) => getScenarioHighlightColor(theme)
                    : 'inherit',
                }}
              >
                {params.row.name}
              </Typography>
              {!owned && (
                <Chip
                  label='Shared'
                  size='small'
                  variant='outlined'
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 160,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: string | null) => value ?? '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<ScenarioDTO>) => (
          <Stack>
            <Chip
              label={params.row.status}
              color={STATUS_COLOR[params.row.status] ?? 'default'}
              size='small'
              variant={
                isViewOrEditing(params.row.status) ? 'filled' : 'outlined'
              }
            />
          </Stack>
        ),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        width: 130,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 175,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: string | null) =>
          value ? new Date(value).toLocaleString() : '—',
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        width: 175,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: string | null) =>
          value ? new Date(value).toLocaleString() : '—',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams<ScenarioDTO>) => {
          const scenario = params.row;
          const owned = isOwnedByMe(scenario);
          const effectiveStatus = getEffectiveStatus(scenario);
          const isDraft = effectiveStatus === 'DRAFT';
          const isView = effectiveStatus === 'VIEW';
          const isEditing = effectiveStatus === 'EDITING';
          const isActive = isView || isEditing;
          const hasViewOrEditingSharedUser = (
            scenario.scenarioSharedUsers ?? []
          ).some((su) => isViewOrEditing(su.status));

          const scenarioIsBeingEdited = scenario.status === 'EDITING';
          const editorIsMe = owned
            ? isEditing
            : (scenario.scenarioSharedUsers ?? []).some(
                (su) =>
                  su.sharedUser.emea === currentUser && su.status === 'EDITING'
              );
          const lockExpired = isEditingLockExpired(scenario.updatedAt);
          const editorName = scenario.updatedBy ?? scenario.createdBy;

          const canTakeover =
            !readOnly && scenarioIsBeingEdited && !editorIsMe && lockExpired;
          const isLockedByOther =
            scenarioIsBeingEdited && !editorIsMe && !lockExpired;

          // -- View button: always present, highlighted when active --
          const viewTooltip = isView
            ? 'Currently viewing'
            : isDraft
              ? 'Activate in View mode'
              : 'Switch to View mode';
          const onViewClick = () => {
            if (isView) return;
            if (isDraft) handleView(scenario);
            else if (isEditing) handleStopEditing(scenario);
          };

          // -- Edit button: always present, highlighted when editing --
          const editTooltip =
            editorIsMe && isEditing
              ? 'Currently editing'
              : isLockedByOther
                ? `Being edited by ${editorName}`
                : canTakeover
                  ? `Take over editing from ${editorName} (lock expired)`
                  : isDraft
                    ? 'Activate in Edit mode'
                    : 'Enter Edit mode';
          const onEditClick = () => {
            if (editorIsMe && isEditing) return;
            if (canTakeover) setTakeoverTarget(scenario);
            else handleEdit(scenario);
          };

          // -- Deactivate button: always present, disabled when already Draft --
          const deactivateTooltip = isDraft
            ? 'Already in Draft'
            : 'Back to Draft';

          const activeBtnSx = (color: string) => ({
            bgcolor: `${color}.main`,
            color: `${color}.contrastText`,
            '&:hover': { bgcolor: `${color}.dark` },
            pointerEvents: 'none' as const,
          });

          return (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              sx={{ width: '100%' }}
            >
              <Tooltip title={viewTooltip}>
                <span>
                  <IconButton
                    aria-label={viewTooltip}
                    color={isView ? 'info' : 'default'}
                    onClick={onViewClick}
                    size='small'
                    sx={isView ? activeBtnSx('info') : undefined}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {!readOnly && (
                <Tooltip title={editTooltip}>
                  <span>
                    <IconButton
                      aria-label={editTooltip}
                      color={editorIsMe && isEditing ? 'warning' : 'default'}
                      disabled={isLockedByOther}
                      onClick={onEditClick}
                      size='small'
                      sx={
                        editorIsMe && isEditing
                          ? activeBtnSx('warning')
                          : undefined
                      }
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip title={deactivateTooltip}>
                  <span>
                    <IconButton
                      aria-label={deactivateTooltip}
                      disabled={isDraft}
                      onClick={() => handleDeactivate(scenario)}
                      size='small'
                    >
                      <ExitToAppIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!readOnly && owned && (
                <Tooltip title='Share scenario'>
                  <span>
                    <IconButton
                      aria-label='Share scenario'
                      onClick={() =>
                        apiRef.current.toggleDetailPanel(scenario.id)
                      }
                      size='small'
                    >
                      <PersonAddOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!readOnly && owned && (
                <Tooltip
                  title={
                    isActive
                      ? 'Cannot delete while active'
                      : hasViewOrEditingSharedUser
                        ? 'Cannot delete — a shared user has it active'
                        : 'Delete scenario'
                  }
                >
                  <span>
                    <IconButton
                      aria-label='Delete scenario'
                      color='error'
                      disabled={isActive || hasViewOrEditingSharedUser}
                      onClick={() => setDeleteTarget(scenario)}
                      size='small'
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeScenario,
      ownedViewOrEditingScenario,
      scenarios,
      currentUser,
      readOnly,
    ]
  );

  return (
    <>
      <MBModal
        open={open}
        onClose={handleClose}
        title='Scenario Manager'
        maxWidth='lg'
        isLoading={isLoading}
        disableClose={isActionInProgress}
      >
        <Stack spacing={3}>
          {!readOnly && (
            <>
              <Box>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  sx={{ mb: 1 }}
                >
                  <Typography variant='h6'>Create New Scenario</Typography>
                  {atLimit && (
                    <Typography variant='caption' color='text.secondary'>
                      {ownedActiveOrDraftCount}/{MAX_OWNED_SCENARIOS} slots used
                    </Typography>
                  )}
                </Stack>
                {atLimit && (
                  <Typography
                    variant='body2'
                    sx={{
                      color: (theme) => getScenarioHighlightColor(theme),
                      mb: 1.5,
                    }}
                  >
                    You have reached the maximum of {MAX_OWNED_SCENARIOS}
                    scenarios. Delete one to create a new scenario.
                  </Typography>
                )}
                <Stack direction='row' spacing={2} alignItems='center'>
                  <TextField
                    label='Name'
                    required
                    size='small'
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    disabled={atLimit}
                    sx={{ flexGrow: 1 }}
                  />
                  <TextField
                    label='Description'
                    size='small'
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    disabled={atLimit}
                    sx={{ flexGrow: 2 }}
                  />
                  <Tooltip
                    title={
                      atLimit
                        ? `You can only have ${MAX_OWNED_SCENARIOS} scenarios. Delete one first.`
                        : ''
                    }
                    disableHoverListener={!atLimit}
                  >
                    <span>
                      <Button
                        variant='contained'
                        startIcon={<Add />}
                        disabled={!newName.trim() || isCreating || atLimit}
                        onClick={handleCreate}
                      >
                        Create Scenario
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>

              <Divider />
            </>
          )}

          <Box>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Typography variant='h6'>
                {readOnly ? 'Shared Scenarios' : 'Your Scenarios'}
              </Typography>
              {!readOnly ? (
                <Tabs
                  value={tabIndex}
                  onChange={(_, v) => setTabIndex(v)}
                  textColor='inherit'
                  indicatorColor='primary'
                  sx={{ minHeight: 36 }}
                >
                  <Tab
                    label={`My Scenarios (${activeOrDraftScenarios.length})`}
                    sx={{ minHeight: 36, py: 0.5 }}
                  />
                  <Tab
                    label={`Shared with me (${sharedScenarios.length})`}
                    sx={{ minHeight: 36, py: 0.5 }}
                  />
                </Tabs>
              ) : null}
            </Stack>
            <DataGridPro
              rows={
                readOnly
                  ? sharedScenarios
                  : tabIndex === 0
                    ? activeOrDraftScenarios
                    : sharedScenarios
              }
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              disableRowSelectionOnClick
              hideFooter
              loading={isLoading}
              slots={{ loadingOverlay: () => <LinearProgress /> }}
              rowHeight={52}
              pinnedColumns={{
                right: ['actions'],
              }}
              apiRef={apiRef}
              {...(!readOnly && tabIndex === 0
                ? {
                    getDetailPanelContent,
                    getDetailPanelHeight,
                  }
                : {})}
              getRowClassName={(params) =>
                activeScenario?.id === params.row.id
                  ? 'scenario-active-row'
                  : ''
              }
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                minHeight: 215,
                '& .scenario-active-row': {
                  // borderLeft: '3px solid',
                  borderLeftColor: (theme) => getScenarioHighlightColor(theme),
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              localeText={{
                noRowsLabel: readOnly
                  ? 'No scenarios have been shared with you.'
                  : tabIndex === 0
                    ? 'No scenarios yet. Create one above.'
                    : 'No scenarios have been shared with you.',
              }}
            />
          </Box>
        </Stack>
      </MBModal>

      <DoubleConfirmModal
        open={!!deleteTarget}
        title='Delete Scenario'
        confirmText='Delete'
        contextText={`Are you sure you want to delete "${deleteTarget?.name}"? All delta changes will be permanently removed.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        isLoading={isDeleting}
        isDisabled={isDeleting}
      />

      <DoubleConfirmModal
        open={!!takeoverTarget}
        title='Take Over Editing'
        confirmText='Take Over'
        contextText={
          `"${takeoverTarget?.name}" is currently being edited by ${takeoverTarget?.updatedBy ?? takeoverTarget?.createdBy}. ` +
          'Taking over will end their editing session and may discard any unsaved changes they have. ' +
          'Please communicate with the current editor before proceeding.'
        }
        onConfirm={handleTakeoverConfirm}
        onCancel={() => setTakeoverTarget(null)}
        onClose={() => setTakeoverTarget(null)}
        isLoading={isLoading}
        isDisabled={isLoading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant='filled'
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScenarioManagerModal;
